import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { Button, ControlLink, SearchInput, Stack, Text, LoadingBalls } from '@moda/om';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { deferUntilNewTitle } from '../../lib/deferUntilNewTitle';
import { FetchError } from '../../lib/authFetch';
import { tc } from '../../lib/trackingContext';
import { hrefFor } from '../../routers';
import { PurchaseOrder } from '../../types/data';
import { PO_STATUS_LABELS } from '../../constants/poStatuses';
import { ErrorPage } from '../../pages/ErrorPage/ErrorPage';
import { usePurchaseOrders } from '../../api/purchaseOrders';

import './PurchaseOrders.scss';

const DEBOUNCE_MS = 300;
const SCROLL_THRESHOLD = 100;
const FORBIDDEN_STATUS = 403;
const CONFIRMED_COLUMN_COUNT = 8;
const UNCONFIRMED_COLUMN_COUNT = 7;
const FORBIDDEN_MESSAGE =
  'Your session is being refreshed or you do not currently have access to purchase orders.';

type Tab = 'confirmed' | 'unconfirmed';

const isValidTab = (value: string | null): value is Tab =>
  value === 'confirmed' || value === 'unconfirmed';

export const PurchaseOrders: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);

  const tabParam = searchParams.get('tab');
  const tab: Tab = isValidTab(tabParam) ? tabParam : 'unconfirmed';
  const searchParam = searchParams.get('search') ?? '';

  const [searchInput, setSearchInput] = React.useState(searchParam);
  const [selectedPoNumber, setSelectedPoNumber] = React.useState('');

  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newParams.set('search', debouncedSearch);
    } else {
      newParams.delete('search');
    }
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [debouncedSearch, searchParams, setSearchParams]);

  const state = tab === 'unconfirmed' ? 'Unconfirmed' : 'Confirmed';
  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    usePurchaseOrders({ search: debouncedSearch, state });

  const purchaseOrders = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.content) as PurchaseOrder[];
  }, [data?.pages]);
  const totalColumns = tab === 'confirmed' ? CONFIRMED_COLUMN_COUNT : UNCONFIRMED_COLUMN_COUNT;

  const totalElements = data?.pages[0]?.totalElements ?? 0;

  useEffect(() => {
    if (!isLoading) {
      deferUntilNewTitle(() => {
        tc.track('Purchase Orders Dashboard Viewed');
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (debouncedSearch) {
      tc.track('Purchase Order Searched', {
        addToScopeOnce: { searchString: debouncedSearch }
      });
    }
  }, [debouncedSearch]);

  const handleScroll = useCallback(() => {
    const scrollContainer = tableBodyRef.current;

    if (!scrollContainer) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const scrollContainer = tableBodyRef.current;

    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleTabClick = useCallback(
    (newTab: Tab) => {
      if (newTab !== tab) {
        const newParams = new URLSearchParams(searchParams);
        if (newTab === 'unconfirmed') {
          newParams.delete('tab');
        } else {
          newParams.set('tab', newTab);
        }
        setSearchParams(newParams);
      }
    },
    [tab, searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
  }, []);

  const handleRowClick = useCallback((poNumber: string) => {
    setSelectedPoNumber(poNumber);
  }, []);

  const formatDate = useCallback((date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const formatStatus = useCallback((text: string) => {
    const label = PO_STATUS_LABELS.find(status => status.statusName === text);
    return label?.displayText ?? text;
  }, []);

  const isShipmentDisabled = (order: PurchaseOrder) =>
    order.status === 'Fully_Received' || order.status === 'Billed' || order.hasRequestedMaxQty;

  if (selectedPoNumber) {
    return <Navigate to={hrefFor.OrderDetailPage({ poNumber: selectedPoNumber })} replace />;
  }

  const isForbiddenError = error instanceof FetchError && error.status === FORBIDDEN_STATUS;

  if (error && !isForbiddenError) throw error;

  return (
    <Stack space={6} className="PurchaseOrders">
      <Stack
        space={1}
        direction="horizontal"
        justifyContent="space-between"
        className="PurchaseOrders__header"
      >
        <Text treatment="h4" color="fog">
          Purchase Orders (POs) Dashboard
        </Text>
        <div className="PurchaseOrders__search-container">
          <SearchInput
            className="PurchaseOrders__search-input"
            aria-label="Search"
            placeholder="Search"
            value={searchInput}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
          />
        </div>
      </Stack>

      <div className="PurchaseOrders__navigation">
        <div className="PurchaseOrders__arrow-bar">
          <button
            onClick={() => handleTabClick('unconfirmed')}
            className={classNames('PurchaseOrders__item', {
              'PurchaseOrders__item--active': tab === 'unconfirmed'
            })}
          >
            <span>Unconfirmed POs</span>
            <span className="PurchaseOrders__arrow" />
          </button>
          <button
            onClick={() => handleTabClick('confirmed')}
            className={classNames('PurchaseOrders__item', {
              'PurchaseOrders__item--active': tab === 'confirmed'
            })}
          >
            <span>Confirmed POs</span>
          </button>
        </div>
      </div>

      <div className="ScrollDiv">
        <table className="PurchaseOrders__orders-table">
          <thead className="PurchaseOrders__thead">
            <tr className="PurchaseOrders__row">
              <th className="PurchaseOrders__row-header">PO Number</th>
              <th className="PurchaseOrders__row-header">Total Units</th>
              <th className="PurchaseOrders__row-header">Total WS Amount</th>
              <th className="PurchaseOrders__row-header">Shipment Terms</th>
              <th className="PurchaseOrders__row-header">Start Ship Date</th>
              <th className="PurchaseOrders__row-header">In Warehouse Date</th>
              <th className="PurchaseOrders__row-header">Status</th>
              {tab === 'confirmed' && <th className="PurchaseOrders__row-header">Actions</th>}
            </tr>
          </thead>

          {isLoading && (
            <tbody>
              <tr>
                <td colSpan={totalColumns}>
                  <LoadingBalls />
                </td>
              </tr>
            </tbody>
          )}

          {!isLoading && totalElements > 0 && (
            <tbody ref={tableBodyRef} className="PurchaseOrders__tbody">
              {purchaseOrders.map((order, index) => (
                <tr
                  className="PurchaseOrders__row"
                  key={order.poNumber || index}
                  onClick={() => handleRowClick(order.poNumber)}
                >
                  <td className="PurchaseOrders__row-data">{order.poNumber}</td>
                  <td className="PurchaseOrders__row-data">{order.totalQuantity}</td>
                  <td className="PurchaseOrders__row-data">{order.totalWholesalePriceFormatted}</td>
                  <td className="PurchaseOrders__row-data">{order.deliveryTerms}</td>
                  <td className="PurchaseOrders__row-data">{formatDate(order.deliveryStart)}</td>
                  <td className="PurchaseOrders__row-data">{formatDate(order.deliveryEnd)}</td>
                  <td className="PurchaseOrders__row-data">
                    <Button className={`PurchaseOrders__status ${order.status}`} chip disabled>
                      {formatStatus(order.status)}
                    </Button>
                  </td>
                  {tab === 'confirmed' && (
                    <td
                      className="PurchaseOrders__row-data"
                      onClick={event => event.stopPropagation()}
                    >
                      <Button
                        className={classNames('PurchaseOrders__create-shipment', {
                          'PurchaseOrders__create-shipment--disabled': isShipmentDisabled(order)
                        })}
                        to={hrefFor.ShipmentPage({ poNumber: order.poNumber })}
                        disabled={isShipmentDisabled(order)}
                      >
                        Create Shipment
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}

          {!isLoading && isForbiddenError && (
            <tbody>
              <tr className="ErrorPage">
                <td colSpan={totalColumns}>
                  <Stack space={2} className="ErrorPage__content">
                    <Text treatment="h3" className="ErrorPage__message">
                      Unable to load purchase orders
                    </Text>
                    <div className="ErrorPage__action-link">
                      <Text>
                        {FORBIDDEN_MESSAGE}{' '}
                        <ControlLink aria-label="Try again" href="." className="ErrorPage__action">
                          Try again
                        </ControlLink>{' '}
                        or{' '}
                        <ControlLink
                          className="ErrorPage__action"
                          href="mailto:retail.ops@modaoperandi.com"
                        >
                          contact us
                        </ControlLink>{' '}
                        if the issue continues.
                      </Text>
                    </div>
                  </Stack>
                </td>
              </tr>
            </tbody>
          )}

          {!isLoading && !isForbiddenError && totalElements === 0 && <ErrorPage />}
        </table>

        {isFetchingNextPage && (
          <div className="PurchaseOrders__loading-more">
            <LoadingBalls />
          </div>
        )}
      </div>
    </Stack>
  );
};
